import { TaggedStructFromUntagged } from '../../../TaggedStructFromUntagged';
import { Array as Arr, Order, Schema as S, SchemaGetter } from 'effect';

/**
 * Boolean filter option
 */
export const BooleanOption = TaggedStructFromUntagged('BooleanOption', {
  label: S.String,
  value: S.Boolean,
});

export type BooleanOption = S.Schema.Type<typeof BooleanOption>;

/**
 * Regular select option structure for select-type attributes
 */
export const RegularSelectOption = TaggedStructFromUntagged('RegularSelectOption', {
  label: S.String,
  value: S.String,
});

export type RegularSelectOption = S.Schema.Type<typeof RegularSelectOption>;

/**
 * Tagged union of all select option types
 */
export const SelectOptionUnion = S.Union([BooleanOption, RegularSelectOption]);

export type SelectOption = S.Schema.Type<typeof SelectOptionUnion>;

export const BaseProductAttributeFields = S.Struct({
  '@id': S.optional(S.String),
  '@type': S.optional(S.String),
  unit: S.Union([S.String, S.Null]),
  filterUrlMode: S.Literals(['path', 'fragment', 'hide']),
  filterState: S.Literals(['open', 'close']),
  numberOfShow: S.Union([S.Finite.check(S.isInt()), S.Null]),
  code: S.String,
  position: S.Finite.check(S.isInt()),
  name: S.Union([S.String, S.Null]),
}).annotate({ identifier: 'ProductAttribute.jsonld-sylius.shop.taxon.show.detail' }).fields;

/**
 * Checkbox ProductAttribute - uses TaggedStructFromUntagged with Chunk of boolean values
 */
export const CheckboxProductAttribute = TaggedStructFromUntagged('CheckboxProductAttribute', {
  ...BaseProductAttributeFields,
  type: S.Literal('checkbox'),
  attributeValues: S.Chunk(S.Boolean),
}).annotate({ identifier: 'CheckboxProductAttribute' });

export type CheckboxProductAttribute = S.Schema.Type<typeof CheckboxProductAttribute>;

/**
 * Integer ProductAttribute - uses TaggedStructFromUntagged with Chunk of number values (integers)
 */
export const IntegerProductAttribute = TaggedStructFromUntagged('IntegerProductAttribute', {
  ...BaseProductAttributeFields,
  type: S.Literal('integer'),
  attributeValues: S.Chunk(S.Finite),
}).annotate({ identifier: 'IntegerProductAttribute' });

export type IntegerProductAttribute = S.Schema.Type<typeof IntegerProductAttribute>;

/**
 * Float ProductAttribute - uses TaggedStructFromUntagged with Chunk of number values (floats)
 */
export const FloatProductAttribute = TaggedStructFromUntagged('FloatProductAttribute', {
  ...BaseProductAttributeFields,
  type: S.Literal('float'),
  attributeValues: S.Chunk(S.Finite),
}).annotate({ identifier: 'FloatProductAttribute' });

export type FloatProductAttribute = S.Schema.Type<typeof FloatProductAttribute>;

/**
 * Textarea ProductAttribute - uses TaggedStructFromUntagged with Chunk of string values
 */
export const TextareaProductAttribute = TaggedStructFromUntagged('TextareaProductAttribute', {
  ...BaseProductAttributeFields,
  type: S.Literal('textarea'),
  attributeValues: S.Chunk(S.String),
}).annotate({ identifier: 'TextareaProductAttribute' });

export type TextareaProductAttribute = S.Schema.Type<typeof TextareaProductAttribute>;

/**
 * Text ProductAttribute - uses TaggedStructFromUntagged with Chunk of string values
 */
export const TextProductAttribute = TaggedStructFromUntagged('TextProductAttribute', {
  ...BaseProductAttributeFields,
  type: S.Literal('text'),
  attributeValues: S.Chunk(S.String),
}).annotate({ identifier: 'TextProductAttribute' });

export type TextProductAttribute = S.Schema.Type<typeof TextProductAttribute>;

/**
 * Select ProductAttribute - uses TaggedStructFromUntagged with Chunk of SelectOption values
 */
export const SelectProductAttribute = TaggedStructFromUntagged('SelectProductAttribute', {
  ...BaseProductAttributeFields,
  type: S.Literal('select'),
  attributeValues: S.Chunk(RegularSelectOption),
}).annotate({ identifier: 'SelectProductAttribute' });

export type SelectProductAttribute = S.Schema.Type<typeof SelectProductAttribute>;

/**
 * Union of all ProductAttribute types - Tagged Union using discriminant 'type'
 */
export const ProductAttribute = S.Union([
  CheckboxProductAttribute,
  IntegerProductAttribute,
  FloatProductAttribute,
  TextareaProductAttribute,
  TextProductAttribute,
  SelectProductAttribute,
]).annotate({ identifier: 'ProductAttributeTaggedUnion' });

export type ProductAttribute = S.Schema.Type<typeof ProductAttribute>;

/**
 * JSON-wire codec for crossing the Server → Client boundary.
 *
 * The plain `ProductAttribute` schema's encoded form retains `Schema.Chunk`
 * instances inside `attributeValues` (see the longer note on
 * `ProductAttributeSortedSetJsonCodec` below). Encode through this codec
 * whenever a `ProductAttribute` value is being passed to a `'use client'`
 * component or otherwise needs to survive `JSON.stringify`.
 */
export const ProductAttributeJsonCodec = S.toCodecJson(ProductAttribute);

export type ProductAttributeEncoded = S.Codec.Encoded<typeof ProductAttributeJsonCodec>;

/**
 * Price range structure
 */
export const PriceRange = S.Struct({
  /** Minimum price in minor units (e.g., cents) */
  min: S.optional(S.Finite),
  /** Maximum price in minor units (e.g., cents) */
  max: S.optional(S.Finite),
});

export type PriceRange = S.Schema.Type<typeof PriceRange>;

/**
 * Price range that can be null (for API compatibility)
 */
export const PriceRangeNullable = S.NullOr(PriceRange);

export type PriceRangeNullable = S.Schema.Type<typeof PriceRangeNullable>;

const productAttributeOrder = Order.mapInput(
  Order.Number,
  (attr: ProductAttribute) => attr.position
);

const sortAndDedupByPosition = (
  attrs: ReadonlyArray<ProductAttribute>
): ReadonlyArray<ProductAttribute> =>
  Arr.dedupeWith(Arr.sort(attrs, productAttributeOrder), (a, b) => a.position === b.position);

export const ProductAttributeSortedSet = S.Array(ProductAttribute).pipe(
  S.decode({
    decode: SchemaGetter.transform(sortAndDedupByPosition),
    encode: SchemaGetter.transform((attrs: ReadonlyArray<ProductAttribute>) => attrs),
  })
);

export type ProductAttributeSortedSet = S.Schema.Type<typeof ProductAttributeSortedSet>;

/**
 * JSON-wire codec for crossing the Server → Client boundary.
 *
 * `Schema.encodeSync(ProductAttributeSortedSet)` preserves `Schema.Chunk`'s
 * declared encoded form, which is *still a Chunk class instance* (see
 * `Schema.d.ts:5315`: `Chunk_.Chunk<Value["Encoded"]>`). RSC's serializer
 * rejects any value whose prototype is not `Object.prototype`, leading to a
 * `Maximum call stack size exceeded` on the error path. `Schema.toCodecJson`
 * walks the schema and substitutes each declared type's `toCodec` annotation,
 * turning `Chunk<boolean>` into `ReadonlyArray<boolean>` and so on.
 *
 * Mirrors `apps/rcprofi/src/effect/schema/order/orderDetail.ts` (commit
 * `4b9a60b9a fix(effect-v4): orderDetail — Schema.toCodecJson for RSC plain-
 * object invariant`).
 */
export const ProductAttributeSortedSetJsonCodec = S.toCodecJson(ProductAttributeSortedSet);

export type ProductAttributeSortedSetEncoded = S.Codec.Encoded<
  typeof ProductAttributeSortedSetJsonCodec
>;

/**
 * Default empty ordered list for ProductAttributes - avoids null checks.
 */
export const emptyProductAttributeSortedSet: ProductAttributeSortedSet = [];

export const decodeProductAttributeSortedSet = S.decodeUnknownSync(
  ProductAttributeSortedSetJsonCodec
);

export const encodeProductAttributeSortedSet = S.encodeSync(ProductAttributeSortedSetJsonCodec);
